import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { analytics } from '../services/analytics';

/**
 * Hooks recording-playback analytics onto an HTML <video> element. One hook
 * covers every place we play a finished live / room recording — VideoPlayer
 * (HLS + MP4), RoomRecordingPlayer (bare native controls), future native
 * players — by sharing the event names and props shape documented in
 * `docs/improvements/observability-server-events.md` §3.
 *
 * Event semantics (kept thin on purpose — BE-side dashboards do the
 * aggregation):
 *   - `recording_play_started` — fired on the FIRST play, OR when the
 *     viewer resumes from a real pause (not from a buffering stall, which
 *     the browser also fires `pause` for; we filter by `video.ended` and
 *     by tracking whether the previous state was actually «user paused»).
 *   - `recording_play_paused` — user-initiated pause. We skip emitting
 *     when `video.ended` is true (the browser fires `pause` right before
 *     `ended`, and we want a single `completed` event in that case).
 *   - `recording_play_completed` — `ended` event. Fired once per session.
 *   - `recording_play_seek` — `seeked` event with non-trivial delta
 *     (>1s) so dragging the scrubber a hair doesn't spam.
 *   - `recording_play_failed` — `error` event. Includes `MediaError.code`
 *     when available + the current `networkState`.
 *
 * No-op on native (Platform.OS !== 'web') because our recording playback
 * is web-only today. Native consumers using Expo Video should call
 * `analytics.track('recording_play_*')` directly until we ship a native
 * player.
 */

export type RecordingPlaybackSource =
  | 'live_detail'
  | 'library'
  | 'feed'
  | 'profile'
  | 'room_summary'
  // CORE-1760 — the new ending page of a room.
  | 'room_ending'
  | 'dashboard'
  // CORE-588 — preview inside the «Публикация в Медиа» modal.
  | 'publish_media';

interface Context {
  /** Provide exactly one. */
  liveId?: number;
  roomId?: number;
  source: RecordingPlaybackSource;
  /** True when the current viewer is the master of the live / host of the
   *  room. Used by BE-side funnels to split «owner re-watches own content»
   *  from «viewer watches someone else's». */
  isOwner?: boolean;
}

const SEEK_DELTA_THRESHOLD_SECONDS = 1;

export function useRecordingPlaybackTelemetry(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  ctx: Context | null,
): void {
  // Stash ctx in a ref so listener identities don't churn every render —
  // the effect's deps are intentionally minimal (just the ref), so the
  // listeners attach once per mount and read the latest ctx at fire time.
  // `null` ctx disables emission (we still mount the hook unconditionally
  // to keep React happy about rules-of-hooks).
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  // Track session state so we can distinguish «first play» from «resume
  // after pause», emit `recording_play_completed` only once, etc.
  const startedRef = useRef(false);
  const completedRef = useRef(false);
  const wasPausedRef = useRef(false);
  const seekFromRef = useRef<number | null>(null);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const video = videoRef.current;
    if (!video) return;

    const baseProps = (): Record<string, any> | null => {
      const c = ctxRef.current;
      if (!c) return null;
      const props: Record<string, any> = { source: c.source };
      if (typeof c.liveId === 'number') props.liveId = c.liveId;
      if (typeof c.roomId === 'number') props.roomId = c.roomId;
      if (typeof c.isOwner === 'boolean') props.isOwner = c.isOwner;
      const dur = video.duration;
      if (Number.isFinite(dur) && dur > 0) props.durationSeconds = Math.round(dur);
      return props;
    };

    const onPlay = () => {
      const base = baseProps();
      if (!base) return;
      // The browser also fires `play` after a buffering stall — filter
      // those out by tracking whether we've already emitted `started`
      // for this session AND we weren't in a user-paused state.
      if (!startedRef.current) {
        startedRef.current = true;
        analytics.track('recording_play_started', base);
      } else if (wasPausedRef.current) {
        wasPausedRef.current = false;
        analytics.track('recording_play_resumed', {
          ...base,
          atSeconds: Math.round(video.currentTime || 0),
        });
      }
    };

    const onPause = () => {
      // Browsers fire `pause` immediately before `ended`. Skip the pause
      // event in that case to keep the «one completed per session» semantic.
      if (video.ended || completedRef.current) return;
      const base = baseProps();
      if (!base) return;
      wasPausedRef.current = true;
      analytics.track('recording_play_paused', {
        ...base,
        atSeconds: Math.round(video.currentTime || 0),
      });
    };

    const onEnded = () => {
      if (completedRef.current) return;
      const base = baseProps();
      if (!base) return;
      completedRef.current = true;
      analytics.track('recording_play_completed', base);
    };

    // `seeking` fires when the scrubber starts moving, `seeked` when it
    // settles. We capture the «from» on seeking and emit on seeked so the
    // event carries both endpoints (useful for spotting users skipping
    // intros vs. genuinely jumping around).
    const onSeeking = () => {
      seekFromRef.current = video.currentTime;
    };

    const onSeeked = () => {
      const from = seekFromRef.current;
      seekFromRef.current = null;
      if (from == null) return;
      const to = video.currentTime;
      if (Math.abs(to - from) < SEEK_DELTA_THRESHOLD_SECONDS) return;
      const base = baseProps();
      if (!base) return;
      analytics.track('recording_play_seek', {
        ...base,
        fromSeconds: Math.round(from),
        toSeconds: Math.round(to),
      });
    };

    const onError = () => {
      const base = baseProps();
      if (!base) return;
      const err = video.error;
      analytics.track('recording_play_failed', {
        ...base,
        errorCode: err?.code ?? null,
        errorMessage: err?.message ?? null,
        networkState: video.networkState,
        readyState: video.readyState,
      });
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onEnded);
    video.addEventListener('seeking', onSeeking);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('error', onError);

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('seeking', onSeeking);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);
    };
  }, [videoRef]);
}
